import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  memo,
} from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useOnOutsideClick } from "@hooks/useOnOutsideClick";
import ArrowDownSvg from "@assets/svgs/ArrowDownSvg";
import GlobeSvg from "@assets/svgs/GlobeSvg";
import styles from "./LanguageSwitcher.module.css";

// Constants
const LANGUAGE_NAMES = {
  en: "English",
  ge: "ქართული",
  ru: "Русский",
};

const LANGUAGE_FULL_NAMES = {
  kk: "Kazakh",
  ru: "Русский",
  en: "English",
  ge: "ქართული",
};

const SUPPORTED_LANGUAGES = ["ge", "en", "ru"];

const LANGUAGE_TYPES = {
  LANDING: "landing",
  HEADER: "header",
};

/**
 * LanguageSwitcher Component
 * Provides language selection functionality with different UI variants
 */
const LanguageSwitcher = ({
  type = LANGUAGE_TYPES.LANDING,
  isOpen2 = false,
  setIsOpen2,
  inlineOptions = false,
}) => {
  const { i18n } = useTranslation();
  const [, setIsOpen] = useState(false);
  const switcherRef = useRef(null);

  // Get current language from localStorage or i18n
  const currentLanguage = useMemo(() => {
    const savedLanguage = localStorage.getItem("language");
    return savedLanguage || i18n.language || "en";
  }, [i18n.language]);

  // Sync localStorage with i18n on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem("language");
    if (savedLanguage && savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  // Change language handler
  const changeLanguage = useCallback(
    (lang) => {
      if (!SUPPORTED_LANGUAGES.includes(lang)) {
        console.warn(`Unsupported language: ${lang}`);
        return;
      }

      try {
        i18n.changeLanguage(lang);
        localStorage.setItem("language", lang);
        setIsOpen(false);
        if (setIsOpen2) {
          setIsOpen2(false);
        }
      } catch (error) {
        console.error("Error changing language:", error);
      }
    },
    [i18n, setIsOpen2],
  );

  // Toggle dropdown based on type
  const toggleDropdown = useCallback(() => {
    if (type === LANGUAGE_TYPES.HEADER && setIsOpen2) {
      setIsOpen2((prev) => !prev);
    }
  }, [type, setIsOpen2]);

  // Handle click outside for header
  const handleClickOutside = useCallback(() => {
    if (type === LANGUAGE_TYPES.HEADER && setIsOpen2) {
      setIsOpen2(false);
    }
  }, [type, setIsOpen2]);

  // Use custom hook for outside click detection
  useOnOutsideClick(switcherRef, handleClickOutside);

  // Handle select change for landing type
  const handleSelectChange = useCallback(
    (e) => {
      changeLanguage(e.target.value);
    },
    [changeLanguage],
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e, lang) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        e.stopPropagation();
        changeLanguage(lang);
      } else if (e.key === "Escape") {
        e.preventDefault();
        if (type === LANGUAGE_TYPES.HEADER && setIsOpen2) {
          setIsOpen2(false);
        }
      }
    },
    [changeLanguage, type, setIsOpen2],
  );

  // Landing type - simplified native select element
  if (type === LANGUAGE_TYPES.LANDING) {
    return (
      <select
        onChange={handleSelectChange}
        value={currentLanguage}
        aria-label={i18n.t("language.select", "Select language")}
        className={styles.landingSelect}
      >
        {SUPPORTED_LANGUAGES.map((lang) => (
          <option key={lang} value={lang}>
            {LANGUAGE_NAMES[lang] || lang.toUpperCase()}
          </option>
        ))}
      </select>
    );
  }

  // Header type - uses button with dropdown
  if (type === LANGUAGE_TYPES.HEADER) {
    const isDropdownOpen = isOpen2 || false;

    return (
      <div
        className={`${styles.switcherContainerHidden} ${inlineOptions ? styles.inlineSwitcher : ""}`}
        ref={switcherRef}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleDropdown();
          }}
          className={styles.languageButtonHidden}
          type="button"
          aria-label={i18n.t("language.selectCurrent", {
            defaultValue: "Select language. Current: {{language}}",
            language: LANGUAGE_FULL_NAMES[currentLanguage] || currentLanguage,
          })}
          aria-expanded={isDropdownOpen}
          aria-haspopup="true"
        >
          <GlobeSvg aria-hidden="true" />
          <span className={styles.languageText}>
            {LANGUAGE_NAMES[currentLanguage] || currentLanguage.toUpperCase()}
          </span>
          <ArrowDownSvg active={isDropdownOpen} aria-hidden="true" />
        </button>
        {isDropdownOpen && (
          <div
            className={`${styles.dropdownMenuHidden} ${inlineOptions ? styles.dropdownMenuInline : ""} ${styles.active}`}
            role="menu"
            aria-label={i18n.t("language.options", "Language options")}
          >
            {SUPPORTED_LANGUAGES.map((lang) => {
              const isSelected = currentLanguage === lang;
              return (
                <div
                  key={lang}
                  className={`${styles.dropdownItemHidden} ${isSelected ? styles.active : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    changeLanguage(lang);
                  }}
                  role="menuitemradio"
                  tabIndex={0}
                  onKeyDown={(e) => handleKeyDown(e, lang)}
                  aria-checked={isSelected}
                  aria-label={i18n.t("language.selectOption", {
                    defaultValue: "Select {{language}}",
                    language: LANGUAGE_FULL_NAMES[lang] || lang,
                  })}
                >
                  <span className={styles.languageCode}>
                    {LANGUAGE_NAMES[lang]}
                  </span>
                  {LANGUAGE_FULL_NAMES[lang] !== LANGUAGE_NAMES[lang] && (
                    <span className={styles.languageName}>
                      {LANGUAGE_FULL_NAMES[lang] || lang}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return null;
};

LanguageSwitcher.propTypes = {
  type: PropTypes.oneOf(Object.values(LANGUAGE_TYPES)),
  isOpen2: PropTypes.bool,
  setIsOpen2: PropTypes.func,
  inlineOptions: PropTypes.bool,
};

export default memo(LanguageSwitcher);
